import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarOff, Plus, Check, X, Clock, Pencil, Loader2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { todayIstIso } from "@/utils/date";

const TYPES = { sick: "Sick Leave", casual: "Casual Leave", comp_off: "Comp Off", unpaid: "Unpaid Leave" };
const STATUS_CLS = {
  awaiting_manager: "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30",
  awaiting_hr: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
  approved: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  rejected: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30",
  cancelled: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30",
};
const fmt = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const n = (v) => (Math.round(v * 10) / 10).toString();
const err = (e, f) => toast.error(formatApiError(e?.response?.data?.detail) || f);

const workingDays = (a, b) => {
  if (!a || !b || b < a) return 0;
  let c = 0;
  for (let d = new Date(`${a}T00:00:00`); d <= new Date(`${b}T00:00:00`); d.setDate(d.getDate() + 1)) if (d.getDay() !== 0) c++;
  return c;
};

function StatusPill({ l }) {
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-md border ${STATUS_CLS[l.status]}`} data-testid={`leave-status-${l.id}`}>{l.status_label}</span>;
}

function Stepper({ l }) {
  const steps = [
    { k: "submitted", label: "Submitted", done: true, by: l.user_name, at: l.submitted_at },
    ...(l.manager_action || l.status === "awaiting_manager" || l.needs_manager ? [{ k: "mgr", label: "Manager approval", done: !!l.manager_action, ok: l.manager_action?.decision === "approve", by: l.manager_action?.by_name, at: l.manager_action?.at, note: l.manager_action?.note, current: l.status === "awaiting_manager" }] : []),
    { k: "hr", label: "HR approval", done: !!l.hr_action, ok: l.hr_action?.decision === "approve", by: l.hr_action?.by_name, at: l.hr_action?.at, note: l.hr_action?.note, current: l.status === "awaiting_hr", skipped: l.status === "rejected" && !l.hr_action },
  ];
  return (
    <ol className="flex flex-wrap items-start gap-x-2 gap-y-2 mt-3" data-testid={`leave-stepper-${l.id}`}>
      {steps.map((s, i) => (
        <li key={s.k} className="flex items-start gap-2">
          <div className={`h-6 w-6 rounded-full grid place-items-center text-xs shrink-0 border ${s.done ? (s.ok === false ? "bg-red-500 border-red-500 text-white" : "bg-emerald-500 border-emerald-500 text-white") : s.current ? "border-orange-500 text-orange-600" : "border-white/20 text-white/40"}`}>
            {s.done ? (s.ok === false ? <X size={12} /> : <Check size={12} />) : s.current ? <Clock size={12} /> : i + 1}
          </div>
          <div className="text-xs leading-tight pr-3">
            <div className="font-semibold">{s.label}</div>
            <div className="text-white/50">{s.done ? [s.ok === false ? "Rejected" : s.k === "submitted" ? "" : "Approved", s.by].filter(Boolean).join(" · ") : l.status === "cancelled" ? "Cancelled" : s.skipped ? "—" : s.current ? "Waiting" : "Pending"}</div>
            {s.note && <div className="text-white/60 italic mt-0.5">“{s.note}”</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function LeaveRow({ l, onCancel, children }) {
  return (
    <div className="glass rounded-xl p-4" data-testid={`leave-row-${l.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{l.type_label}</span>
            <StatusPill l={l} />
            <span className="text-xs text-white/50">{l.duration === "half" ? "Half day" : `${n(l.days)} day${l.days === 1 ? "" : "s"}`}</span>
          </div>
          <div className="text-sm mt-1">{l.user_name && children ? <b className="mr-1.5">{l.user_name}</b> : null}{fmt(l.from_date)}{l.to_date !== l.from_date ? ` → ${fmt(l.to_date)}` : ""}</div>
          <div className="text-sm text-white/70 mt-1">Reason: {l.reason}</div>
        </div>
        <div className="flex gap-2">
          {onCancel && ["awaiting_manager", "awaiting_hr"].includes(l.status) && (
            <Button size="sm" variant="outline" onClick={() => onCancel(l)} data-testid={`leave-cancel-${l.id}`} className="border-white/15 bg-transparent text-white hover:bg-white/5">
              <Undo2 size={13} className="mr-1" /> Cancel
            </Button>
          )}
          {children}
        </div>
      </div>
      <Stepper l={l} />
    </div>
  );
}

function BalanceCards({ b }) {
  if (!b) return null;
  const month = todayIstIso().slice(0, 7);
  const cards = [
    ["Casual leave", `${n(b.taken.casual)} / ${n(b.allowance.casual)}`, `${n(b.remaining.casual)} left`, "casual"],
    ["Sick leave", `${n(b.taken.sick)} / ${n(b.allowance.sick)}`, `${n(b.remaining.sick)} left`, "sick"],
    ["Total this year", `${n(b.total_taken)} / ${n(b.total_allowed)}`, `${n(b.total_allowed - b.total_taken)} left`, "total"],
    ["This month", n(b.by_month[month] || 0), "days taken", "month"],
    ["Unpaid leave", n(b.taken.unpaid), "days this year", "unpaid"],
    ["Comp-off", n(b.comp_off.balance), `earned ${n(b.comp_off.earned)} · used ${n(b.comp_off.used)}`, "comp"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
      {cards.map(([l, v, sub, k]) => (
        <div key={k} className="glass rounded-xl p-4" data-testid={`leave-balance-${k}`}>
          <div className="text-xs text-white/55">{l}</div>
          <div className="text-2xl font-display mt-1">{v}</div>
          <div className="text-xs text-white/50 mt-0.5">{sub}</div>
        </div>
      ))}
    </div>
  );
}

function ApplyDialog({ open, onOpenChange, balance, onDone }) {
  const today = todayIstIso();
  const [f, setF] = useState({ leave_type: "casual", duration: "full", from_date: today, to_date: today, reason: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ leave_type: "casual", duration: "full", from_date: today, to_date: today, reason: "" }); }, [open, today]);
  const days = f.duration === "half" ? 0.5 : workingDays(f.from_date, f.to_date);
  const avail = balance ? { casual: balance.remaining.casual, sick: balance.remaining.sick, comp_off: balance.comp_off.balance, unpaid: null }[f.leave_type] : null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leaves", { ...f, to_date: f.duration === "half" ? f.from_date : f.to_date });
      toast.success("Leave request submitted");
      onOpenChange(false); onDone();
    } catch (x) { err(x, "Could not submit leave"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg" data-testid="leave-apply-dialog">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle className="font-display">Apply for leave</DialogTitle>
            <DialogDescription className="text-white/55">Goes to your manager, then HR (Akanksha) for approval.</DialogDescription>
          </DialogHeader>
          <div>
            <div className="text-sm font-medium mb-1.5">Type of leave</div>
            <div className="grid grid-cols-2 gap-1.5">
              {Object.entries(TYPES).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={f.leave_type === k} onClick={() => setF({ ...f, leave_type: k })} data-testid={`leave-type-${k}`}
                  className={`h-10 rounded-md border text-sm transition-colors ${f.leave_type === k ? "border-[#E11D2E]/60 bg-[#E11D2E]/10 font-semibold" : "border-white/10 text-white/70 hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
            {avail != null && <div className="text-xs text-white/55 mt-1.5" data-testid="leave-available">Available: {n(avail)} day(s)</div>}
          </div>
          <div>
            <div className="text-sm font-medium mb-1.5">Duration</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[["full", "Full day"], ["half", "Half day"]].map(([k, l]) => (
                <button key={k} type="button" aria-pressed={f.duration === k} onClick={() => setF({ ...f, duration: k, to_date: k === "half" ? f.from_date : f.to_date })} data-testid={`leave-duration-${k}`}
                  className={`h-10 rounded-md border text-sm transition-colors ${f.duration === k ? "border-[#E11D2E]/60 bg-[#E11D2E]/10 font-semibold" : "border-white/10 text-white/70 hover:bg-white/5"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm font-medium">From
              <Input type="date" value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value, to_date: f.duration === "half" || f.to_date < e.target.value ? e.target.value : f.to_date })}
                data-testid="leave-from" className="mt-1.5 bg-surface border-white/15" required />
            </label>
            <label className="text-sm font-medium">To
              <Input type="date" value={f.duration === "half" ? f.from_date : f.to_date} min={f.from_date} disabled={f.duration === "half"}
                onChange={(e) => setF({ ...f, to_date: e.target.value })} data-testid="leave-to" className="mt-1.5 bg-surface border-white/15" required />
            </label>
          </div>
          <div className="text-sm" data-testid="leave-days-preview">Working days (Mon–Sat): <b>{n(days)}</b></div>
          <label className="text-sm font-medium block">Reason
            <Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3} required minLength={3}
              placeholder="Why do you need this leave?" data-testid="leave-reason" className="mt-1.5 bg-surface border-white/15" />
          </label>
          <DialogFooter>
            <Button type="submit" disabled={busy || days <= 0 || f.reason.trim().length < 3} data-testid="leave-submit" className="bg-[#E11D2E] hover:bg-[#F03041] text-white">
              {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />} Submit request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DecideButtons({ l, onDone }) {
  const act = async (decision) => {
    const note = decision === "reject" ? window.prompt("Reason for not approving (optional)") : "";
    if (note === null) return;
    try {
      await api.post(`/leaves/${l.id}/decision`, { decision, note });
      toast.success(decision === "approve" ? "Approved" : "Marked Not Approved");
      window.dispatchEvent(new Event("lim:nudges-refresh"));
      onDone();
    } catch (x) { err(x, "Action failed"); }
  };
  return (
    <>
      <Button size="sm" onClick={() => act("approve")} data-testid={`leave-approve-${l.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check size={14} className="mr-1" /> Approve</Button>
      <Button size="sm" variant="outline" onClick={() => act("reject")} data-testid={`leave-reject-${l.id}`} className="border-red-500/40 bg-transparent text-red-600 hover:bg-red-500/10"><X size={14} className="mr-1" /> Not approve</Button>
    </>
  );
}

function EditBalanceDialog({ row, year, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (row) setF({ allowance_casual: row.allowance.casual, allowance_sick: row.allowance.sick, taken_casual: row.taken.casual, taken_sick: row.taken.sick, taken_unpaid: row.taken.unpaid, comp_off_balance: row.comp_off.balance });
  }, [row]);
  if (!row || !f) return null;
  const field = (k, l) => (
    <label className="text-sm font-medium">{l}
      <Input type="number" step="0.5" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`leave-edit-${k}`} className="mt-1 bg-surface border-white/15" />
    </label>
  );
  const save = async () => {
    try {
      const body = { year };
      Object.entries(f).forEach(([k, v]) => { body[k] = v === "" ? null : Number(v); });
      ["taken_casual", "taken_sick", "taken_unpaid"].forEach((k) => {
        const key = k.replace("taken_", "");
        if (Number(f[k]) === row.computed_taken[key] && !row.manual[key]) delete body[k];
      });
      if (Number(f.comp_off_balance) === row.comp_off.balance) delete body.comp_off_balance;
      await api.patch(`/leaves/balance/${row.user_id}`, body);
      toast.success("Balance updated"); onSaved();
    } catch (x) { err(x, "Update failed"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-lg" data-testid="leave-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit leave balance — {row.name}</DialogTitle>
          <DialogDescription className="text-white/55">{year}. "Taken" values override the automatic count (enter -1 to go back to automatic).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {field("allowance_casual", "Casual allowance")}{field("allowance_sick", "Sick allowance")}
          {field("taken_casual", "Casual taken")}{field("taken_sick", "Sick taken")}
          {field("taken_unpaid", "Unpaid taken")}{field("comp_off_balance", "Comp-off left")}
        </div>
        <DialogFooter><Button onClick={save} data-testid="leave-edit-save" className="bg-[#E11D2E] hover:bg-[#F03041] text-white">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamTab({ canAdmin }) {
  const [year, setYear] = useState(Number(todayIstIso().slice(0, 4)));
  const [rows, setRows] = useState([]);
  const [all, setAll] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    try {
      const [b, a] = await Promise.all([api.get("/leaves/balances", { params: { year } }), api.get("/leaves/all", { params: { year } })]);
      setRows(b.data); setAll(a.data);
    } catch (x) { err(x, "Failed to load"); }
  }, [year]);
  useEffect(() => { load(); }, [load]);
  const month = todayIstIso().slice(0, 7);
  return (
    <div className="space-y-5" data-testid="leave-team-tab">
      <label className="text-sm font-medium">Year
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="leave-year" className="mt-1 h-10 w-28 bg-surface border-white/15" />
      </label>
      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="border-b border-white/10 text-left text-xs text-white/55">
            {["Person", "Team", "Casual", "Sick", "Total", "This month", "Unpaid", "Comp-off (earned / used / left)", ""].map((h) => <th key={h} className="px-3 py-2.5 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-b border-white/5 last:border-0" data-testid={`leave-balance-row-${r.user_id}`}>
                <td className="px-3 py-2 font-semibold">{r.name}</td>
                <td className="px-3 py-2 text-white/60">{r.team}</td>
                <td className="px-3 py-2">{n(r.taken.casual)} / {n(r.allowance.casual)}{r.manual.casual && <span className="text-xs text-orange-600 ml-1">(edited)</span>}</td>
                <td className="px-3 py-2">{n(r.taken.sick)} / {n(r.allowance.sick)}{r.manual.sick && <span className="text-xs text-orange-600 ml-1">(edited)</span>}</td>
                <td className="px-3 py-2">{n(r.total_taken)} / {n(r.total_allowed)}</td>
                <td className="px-3 py-2">{n(r.by_month[month] || 0)}</td>
                <td className="px-3 py-2">{n(r.taken.unpaid)}</td>
                <td className="px-3 py-2">{n(r.comp_off.earned)} / {n(r.comp_off.used)} / <b>{n(r.comp_off.balance)}</b></td>
                <td className="px-3 py-2 text-right">
                  {canAdmin && <Button size="sm" variant="outline" onClick={() => setEdit(r)} data-testid={`leave-edit-${r.user_id}`} title="Edit balance" className="h-8 border-white/15 bg-transparent text-white hover:bg-white/5"><Pencil size={13} /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h2 className="text-base font-semibold mb-2">All requests · {year}</h2>
        {all.length === 0 ? <div className="text-sm text-white/50">No leave requests yet.</div> : (
          <div className="space-y-3">{all.map((l) => <LeaveRow key={l.id} l={l}><span /></LeaveRow>)}</div>
        )}
      </div>
      <EditBalanceDialog row={edit} year={year} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}

export default function Leaves() {
  const { user } = useAuth();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "mine");
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [balance, setBalance] = useState(null);
  const [meta, setMeta] = useState({});
  const [applyOpen, setApplyOpen] = useState(false);
  const canSeeTeam = meta.can_admin || user?.is_admin || user?.role === "manager";

  const load = useCallback(async () => {
    try {
      const [m, p, b, mt] = await Promise.all([api.get("/leaves/mine"), api.get("/leaves/approvals"), api.get("/leaves/balance"), api.get("/leaves/meta")]);
      setMine(m.data); setPending(p.data); setBalance(b.data); setMeta(mt.data);
    } catch (x) { err(x, "Failed to load leaves"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancel = async (l) => {
    if (!window.confirm("Cancel this leave request?")) return;
    try { await api.delete(`/leaves/${l.id}`); toast.success("Request cancelled"); load(); } catch (x) { err(x, "Cancel failed"); }
  };
  const tabs = useMemo(() => [
    ["mine", "My leaves"],
    ["approvals", `Approvals${pending.length ? ` · ${pending.length}` : ""}`],
    ...(canSeeTeam ? [["team", "Team & balances"]] : []),
  ], [pending.length, canSeeTeam]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="leaves-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline flex items-center gap-2"><CalendarOff size={13} /> Leaves</div>
          <h1 className="font-display mt-1">Leave requests</h1>
          <p className="text-sm text-white/55 mt-1">12 leaves a year (6 casual + 6 sick). Comp-off is earned from overtime in your Daily Log (½ day OT = 0.5, full day OT = 1).</p>
        </div>
        <Button onClick={() => setApplyOpen(true)} data-testid="leave-apply-btn" className="bg-[#E11D2E] hover:bg-[#F03041] text-white"><Plus size={15} className="mr-1" /> Apply for leave</Button>
      </div>
      <BalanceCards b={balance} />
      <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-white/10 p-1 bg-surface" role="tablist">
        {tabs.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`leaves-tab-${k}`}
            className={`h-8 px-3 whitespace-nowrap rounded text-sm transition-colors ${tab === k ? "bg-[#E11D2E]/10 text-[#E11D2E] font-semibold" : "text-white/60 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "mine" && (
        mine.length === 0 ? <div className="glass rounded-xl p-6 text-sm text-white/50 text-center" data-testid="leaves-empty">No leave requests yet.</div>
          : <div className="space-y-3">{mine.map((l) => <LeaveRow key={l.id} l={{ ...l, user_name: "" }} onCancel={cancel} />)}</div>
      )}
      {tab === "approvals" && (
        pending.length === 0 ? <div className="glass rounded-xl p-6 text-sm text-white/50 text-center" data-testid="approvals-empty">Nothing waiting for your approval.</div>
          : <div className="space-y-3">{pending.map((l) => <LeaveRow key={l.id} l={l}><DecideButtons l={l} onDone={load} /></LeaveRow>)}</div>
      )}
      {tab === "team" && canSeeTeam && <TeamTab canAdmin={!!meta.can_admin} />}
      <ApplyDialog open={applyOpen} onOpenChange={setApplyOpen} balance={balance} onDone={load} />
    </div>
  );
}
